import { forwardRef, type CSSProperties } from 'react';
import { useDraggable, type DraggableAttributes } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { CSS } from '@dnd-kit/utilities';
import { CalendarClock, Check, Clock, GripVertical, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { cn } from '@/shared/lib/utils';
import { formatOrderNumber, formatOrderNumberCompact } from '@/shared/lib/orderDisplay';
import { useNotificationsStore } from '@/features/notifications';
import { OrderStatusBadge } from '../OrderStatusBadge';
import { OrderTypePill } from '../OrderTypePill';
import { PaymentTypeChip } from '../PaymentTypeChip';
import { CancelledByLabel } from '../CancelledByLabel';
import { AutoAcceptCountdown } from '../AutoAcceptCountdown';
import { DwellClock } from '../DwellClock';
import { OrderTimingLine } from '../OrderTimingLine';
import { toast } from 'sonner';
import { useAcceptOrder, useAdvanceOrder, useStartPreparing } from '../../hooks/useOrders';
import { OrderActionError } from '../../api';
import { formatMoney } from '../../utils/formatters';
import { getNextLifecycleAction, isTerminalStatus } from '../../utils/lifecycle';
import type { OrderItemResponseDTO, OrderResponse } from '../../types';

const MAX_ITEMS_PREVIEW = 3;

interface Props {
  order: OrderResponse;
  draggable: boolean;
  restaurantId: string;
  onOpen: (order: OrderResponse) => void;
}

/**
 * Compact draggable order card used in Kanban columns. Drag = primary status
 * transition (accept / advance). Click opens the full detail page. Uses a
 * 150ms press-hold activation so quick taps still register as a click.
 */
export function BoardOrderCard({ order, draggable, restaurantId, onOpen }: Props) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: order.orderId,
    data: { order },
    disabled: !draggable,
  });

  const { t } = useTranslation();
  const highlighted = useNotificationsStore((s) => s.highlightedOrderId === order.orderId);
  const advance = useAdvanceOrder(restaurantId);
  const accept = useAcceptOrder(restaurantId);
  const startPreparing = useStartPreparing(restaurantId);
  const nextAction = getNextLifecycleAction(order);

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 40 : undefined,
    opacity: isDragging ? 0.85 : undefined,
  };

  // PENDING → CONFIRMED goes through the accept endpoint (not advance) so the
  // server can auth/capture payment. Everything else uses the lifecycle-aware
  // advance action.
  let quickActionLabel: string | undefined;
  let runQuickAction: (() => void) | undefined;
  let quickActionPending = false;

  if (order.orderStatus === 'PENDING') {
    quickActionLabel = t('orders.accept');
    quickActionPending = accept.isPending;
    runQuickAction = () => {
      accept.mutate(order.orderId, {
        onError: (err) => {
          // Same payment-capture-failed surfacing as the drag path; one tap
          // to jump into the detail screen where retry / reject live.
          if (err instanceof OrderActionError && err.code === 'payment_capture_failed') {
            toast.error('Payment capture failed — open the order to retry.', {
              action: { label: 'Open', onClick: () => onOpen(order) },
              closeButton: true,
            });
          }
        },
      });
    };
  } else if (order.orderStatus === 'SCHEDULED') {
    quickActionLabel = t('orders.action.startPreparing');
    quickActionPending = startPreparing.isPending;
    runQuickAction = () => {
      const clearReminder = useNotificationsStore.getState().clearReminder;
      startPreparing.mutate(order.orderId, {
        onSuccess: () => clearReminder(order.orderId),
      });
    };
  } else if (nextAction) {
    quickActionLabel = t(nextAction.labelKey);
    quickActionPending = advance.isPending;
    runQuickAction = () =>
      advance.mutate({ orderId: order.orderId, targetStatus: nextAction.target });
  }

  return (
    <BoardOrderCardView
      ref={setNodeRef}
      order={order}
      highlighted={highlighted}
      isDragging={isDragging}
      draggable={draggable}
      style={style}
      dragAttributes={attributes}
      dragListeners={listeners}
      onOpen={onOpen}
      nextActionLabel={quickActionLabel}
      onAdvance={runQuickAction}
      advancePending={quickActionPending}
    />
  );
}

interface ViewProps {
  order: OrderResponse;
  highlighted?: boolean;
  isDragging?: boolean;
  /** Grayed-out ghost variant used by the DragOverlay. */
  overlay?: boolean;
  draggable: boolean;
  style?: CSSProperties;
  dragAttributes?: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
  onOpen?: (order: OrderResponse) => void;
  /** Next lifecycle step label, e.g. "Start Preparing". Omitted on the drag ghost. */
  nextActionLabel?: string;
  onAdvance?: () => void;
  advancePending?: boolean;
}

export const BoardOrderCardView = forwardRef<HTMLDivElement, ViewProps>(
  function BoardOrderCardView(
    {
      order,
      highlighted,
      isDragging,
      overlay,
      draggable,
      style,
      dragAttributes,
      dragListeners,
      onOpen,
      nextActionLabel,
      onAdvance,
      advancePending,
    },
    ref
  ) {
    const { t } = useTranslation();
    const badgeLabel = formatOrderNumberCompact(order);
    const orderLabel = formatOrderNumber(order);
    const itemCount = order.orderItems?.reduce((a, i) => a + i.quantity, 0) ?? 0;
    const tone =
      order.orderStatus === 'SCHEDULED'
        ? 'border-l-amber-400/80'
        : order.orderStatus === 'PENDING'
          ? 'border-l-destructive/70'
          : order.orderStatus === 'CONFIRMED'
            ? 'border-l-success/70'
            : order.orderStatus === 'PREPARING'
              ? 'border-l-accent/70'
              : order.orderStatus === 'IN_DELIVERY' || order.orderStatus === 'READY_FOR_PICKUP'
                ? 'border-l-amber-500/70'
                : 'border-l-border';

    return (
      <Card
        ref={ref}
        style={style}
        className={cn(
          'group w-full border-l-4 transition-all touch-none overflow-hidden',
          tone,
          order.orderType === 'PICKUP' && 'bg-amber-50/70 dark:bg-amber-950/20',
          draggable && 'cursor-grab active:cursor-grabbing',
          isDragging && 'shadow-xl ring-2 ring-accent/60',
          overlay && 'shadow-2xl rotate-1',
          highlighted && !isDragging && 'ring-2 ring-accent shadow-card-hover'
        )}
      >
        <CardContent className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <button
              type="button"
              onClick={() => onOpen?.(order)}
              className={cn(
                'flex items-center gap-2 min-w-0 flex-1 text-left',
                // Inherit the grab/grabbing cursor from the Card; the inner
                // <button> would otherwise force `cursor: pointer` and swallow
                // the drag affordance.
                draggable && 'cursor-inherit',
              )}
              aria-label={`Open order ${orderLabel}`}
            >
              <span className="px-1.5 min-w-8 h-8 rounded-md bg-muted flex items-center justify-center font-mono text-[11px] font-bold text-foreground shrink-0">
                {badgeLabel}
              </span>
              <div className="min-w-0">
                <p className="font-medium text-sm leading-tight break-words">
                  {order.customerInfo?.name || t('orders.guest')}
                </p>
                {order.orderStatus === 'SCHEDULED' ? (
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                    <CalendarClock className="w-3 h-3 shrink-0" />
                    <span>{formatScheduledDate(order.scheduledFor, t)}</span>
                  </div>
                ) : isTerminalStatus(order.orderStatus) ? (
                  <OrderTimingLine order={order} compact className="mt-1" />
                ) : (
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <DwellClock order={order} compact />
                    <OrderTimingLine order={order} compact />
                  </div>
                )}
              </div>
            </button>
            {draggable && (
              <button
                type="button"
                className="shrink-0 p-1 -m-1 text-muted-foreground/50 hover:text-muted-foreground focus:text-foreground focus:outline-none cursor-grab active:cursor-grabbing"
                aria-label={t('orders.dragAriaLabel')}
                {...(dragAttributes ?? {})}
                {...(dragListeners ?? {})}
              >
                <GripVertical className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 min-w-0 flex-wrap">
            <div className="min-w-0 shrink">
              <OrderStatusBadge
                status={order.orderStatus}
                autoAcceptedBySystem={order.autoAcceptedBySystem}
                pulse={order.orderStatus === 'PENDING'}
              />
            </div>
            <span className="shrink-0 text-xs font-semibold tabular-nums">{formatMoney(order.total)}</span>
          </div>

          <div className="flex items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground flex-wrap">
            <OrderTypePill type={order.orderType} />
            <span>·</span>
            <span>{t('orders.items', { count: itemCount })}</span>
            {order.paymentType && (
              <>
                <span>·</span>
                <PaymentTypeChip type={order.paymentType} status={order.paymentStatus} />
              </>
            )}
          </div>

          {(order.orderItems?.length ?? 0) > 0 && (
            <ItemsPreview items={order.orderItems!} />
          )}

          {order.orderStatus === 'SCHEDULED' && order.scheduledFor && (
            <div className="flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 rounded px-2 py-1">
              <Clock className="w-3 h-3 shrink-0" />
              {formatCountdown(order.scheduledFor, t)}
            </div>
          )}
          {order.orderStatus === 'PENDING' && order.autoAcceptAt && (
            <AutoAcceptCountdown autoAcceptAt={order.autoAcceptAt} />
          )}

          {/* One-tap advance — mirrors the drag path for users who prefer
              buttons. Hidden on the drag ghost and when the order has no
              forward action (PENDING, COMPLETED, CANCELLED). */}
          {!overlay && nextActionLabel && onAdvance && (
            <Button
              size="sm"
              className="w-full h-8 gap-1.5 mt-0.5 cursor-pointer"
              disabled={advancePending}
              onClick={(e) => {
                e.stopPropagation();
                onAdvance();
              }}
              // The drag listeners are attached to the grip button, not the
              // card, so pointerDown here doesn't start a drag — but we stop
              // propagation anyway so the surrounding click-to-open region
              // never fires on button use.
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={nextActionLabel}
            >
              {advancePending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
              ) : (
                <Check className="w-3.5 h-3.5" aria-hidden />
              )}
              <span>{nextActionLabel}</span>
            </Button>
          )}

          {order.orderStatus === 'CANCELLED' && (order.cancelledBy || order.rejectionReason) && (
            <div className="space-y-1">
              {order.cancelledBy && (
                <p className="text-[11px] text-muted-foreground">
                  Cancelled <CancelledByLabel order={order} />
                </p>
              )}
              {order.rejectionReason && (
                <p className="text-[11px] text-muted-foreground italic line-clamp-2 border-l-2 border-border pl-2">
                  "{order.rejectionReason}"
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
);

function formatScheduledDate(
  scheduledFor: string | null | undefined,
  t: (key: string, opts?: Record<string, string | number>) => string
): string {
  if (!scheduledFor) return '—';
  const date = new Date(scheduledFor);
  if (isNaN(date.getTime())) return scheduledFor;
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const isToday = new Date().toDateString() === date.toDateString();
  if (isToday) return t('orders.date.today', { time });
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  if (tomorrow.toDateString() === date.toDateString()) return t('orders.date.tomorrow', { time });
  return date.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatCountdown(
  scheduledFor: string,
  t: (key: string, opts?: Record<string, string | number>) => string
): string {
  const releaseMs = new Date(scheduledFor).getTime();
  const diffMs = releaseMs - Date.now();
  if (diffMs <= 0) return t('orders.countdown.releasing');
  const mins = Math.ceil(diffMs / 60_000);
  if (mins < 60) return t('orders.countdown.minutes', { count: mins });
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0
    ? t('orders.countdown.hoursMinutes', { hours, mins: rem })
    : t('orders.countdown.hours', { count: hours });
}

/**
 * Compact items list for the board card. One line per item with quantity,
 * name, variant, add-on count, and line subtotal; truncates past three lines
 * with a "+N more" tail so the card's vertical rhythm stays predictable.
 */
function ItemsPreview({ items }: { items: OrderItemResponseDTO[] }) {
  const { t } = useTranslation();
  const shown = items.slice(0, MAX_ITEMS_PREVIEW);
  const remaining = items.length - shown.length;
  return (
    <ul className="min-w-0 space-y-0.5 text-[11px] text-muted-foreground">
      {shown.map((item, i) => {
        const addOnCount = item.selectedAddOns?.length ?? 0;
        const note = item.itemNotes?.trim();
        return (
          <li key={`${item.menuItemId}-${i}`} className="min-w-0">
            <div className="flex items-start justify-between gap-2 min-w-0">
              <span className="min-w-0 break-words">
                <span className="font-semibold text-foreground tabular-nums">
                  {item.quantity}×
                </span>{' '}
                <span className="text-foreground">{item.menuItemName ?? item.menuItemId}</span>
                {item.variantName && (
                  <span className="text-muted-foreground/70"> · {item.variantName}</span>
                )}
                {addOnCount > 0 && (
                  <span className="text-muted-foreground/70">
                    {' '}({t('orders.addOns', { count: addOnCount })})
                  </span>
                )}
              </span>
              {item.subtotal != null && (
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {formatMoney(item.subtotal)}
                </span>
              )}
            </div>
            {note && (
              <p className="mt-0.5 pl-4 text-[10px] italic text-muted-foreground/80 line-clamp-2 border-l-2 border-border ml-1">
                <span className="pl-1.5">"{note}"</span>
              </p>
            )}
          </li>
        );
      })}
      {remaining > 0 && (
        <li className="text-muted-foreground/70">{t('orders.moreItems', { count: remaining })}</li>
      )}
    </ul>
  );
}
